import { useEffect, useRef, useState } from "react";
import { Page } from "../App";
import IndiaMapHero from "../components/IndiaMapHero";
import "./Landing.css";

const ATR_GRADIENT = "linear-gradient(135deg, #009A84 0%, #56C177 50%, #99D6EA 100%)";

const STATS = [
  { value: "$2B", label: "Invested", sub: "In regional connectivity under UDAN" },
  { value: "650+", label: "New routes", sub: "Already operationalised" },
  { value: "230", label: "Airports target", sub: "India's goal by 2030" },
];

const TICKER_ITEMS = [
  "4.6B journeys per year across India",
  "90‑minute drive time defines air demand",
  "150M → 240M domestic passengers",
  "900 new routes identified",
  "35M passengers on regional routes",
  "+85 airports in the 200‑airport scenario",
  "600M people within reach of air services",
];

export default function Landing({ navigate }: { navigate: (p: Page) => void }) {
  const [loaded, setLoaded] = useState(false);
  const [activeIdx, setActiveIdx] = useState(0);

  const targets = STATS.map(s => parseFloat(s.value.replace(/[^0-9.]/g, '')) || 0);
  const [counts, setCounts] = useState<number[]>([...targets]);
  const animFrameId = useRef<number | null>(null);

  const formatValue = (index: number, num: number) => {
    const raw = STATS[index].value;
    const prefix = raw.match(/^[^0-9]*/)?.[0] || '';
    const suffix = raw.match(/[^0-9]*$/)?.[0] || '';
    return `${prefix}${num}${suffix}`;
  };

  const startAnimation = (index: number) => {
    if (animFrameId.current) {
      cancelAnimationFrame(animFrameId.current);
      animFrameId.current = null;
    }

    const target = targets[index];
    if (target === 0) return;

    setCounts(prev => {
      const newCounts = [...prev];
      newCounts[index] = 0;
      return newCounts;
    });

    const duration = 1800;
    const startTime = performance.now();

    const update = (now: number) => {
      const progress = Math.min((now - startTime) / duration, 1);
      const eased = 1 - Math.pow(1 - progress, 3);
      const currentVal = Math.round(target * eased);

      setCounts(prev => {
        const newCounts = [...prev];
        newCounts[index] = currentVal;
        return newCounts;
      });

      if (progress < 1) {
        animFrameId.current = requestAnimationFrame(update);
      } else {
        setCounts(prev => {
          const newCounts = [...prev];
          newCounts[index] = target;
          return newCounts;
        });
        animFrameId.current = null;
      }
    };

    animFrameId.current = requestAnimationFrame(update);
  };

  useEffect(() => {
    startAnimation(activeIdx);
    return () => {
      if (animFrameId.current) {
        cancelAnimationFrame(animFrameId.current);
        animFrameId.current = null;
      }
    };
  }, [activeIdx]);

  useEffect(() => {
    setTimeout(() => setLoaded(true), 100);
    const iv = setInterval(() => setActiveIdx((i) => (i + 1) % STATS.length), 2800);
    return () => clearInterval(iv);
  }, []);

  return (
    <div className="lp">
      <div className="lp-aurora"><span /></div>
      <div className="lp-bg-photo">
        <img src="/photos/ATR-158496_BD.jpg" alt="" />
        <div className="lp-bg-overlay" />
      </div>
      <div className="lp-grain" />

      <div className="lp-corner lp-corner--tl" />
      <div className="lp-corner lp-corner--br" />

      <div className="lp-hero">
        <div className={`lp-left ${loaded ? "lp-left--in" : ""}`}>
          <h1 className="lp-title">
            <span className="lp-title-line" style={{ 
              "--line-delay": "0.15s",
              background: 'linear-gradient(135deg, #009A84 0%, #56C177 50%, #99D6EA 100%)',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              backgroundClip: 'text',
              filter: 'drop-shadow(0 0 30px rgba(0, 154, 132, 0.3))',
            } as React.CSSProperties}>
              Connecting <span className="lp-title-hl">1.4 billion</span> people
            </span>
          </h1>

          <div className="lp-desc-wrapper">
            <p className="lp-desc">
              India is undertaking one of the world's most ambitious aviation expansion programmes through the UDAN scheme,
              with over <span className="highlight-green">USD 2 billion</span> invested, more than{' '}
              <span className="highlight-yellow">650 new routes</span> launched, and a target of{' '}
              <span className="highlight-red">230 airports</span> by 2030.
            </p>
            <p className="lp-desc">
              As connectivity continues to grow, <span className="highlight-cyan">data‑driven insights</span> become essential to understand airport accessibility, catchment areas, route viability, and passenger demand.
            </p>
            <p className="lp-desc">
              Explore this webapp to see how <span className="highlight-yellow">ATR's MobilityMonitor</span> platform can support these analyses and contribute to informed discussions on the future of regional air transport in India.
            </p>
          </div>

          <div className="lp-stats">
            {STATS.map((s, i) => (
              <div
                key={i}
                className={`lp-stat ${activeIdx === i ? "lp-stat--on" : ""}`}
                onMouseEnter={() => setActiveIdx(i)}
              >
                <div className="lp-stat-bar" />
                <div className="lp-stat-content">
                  <div className="lp-stat-line">
                    <span className="lp-stat-val">{formatValue(i, counts[i])}</span>
                    <span className="lp-stat-label">{s.label}</span>
                  </div>
                  <span className="lp-stat-sub">{s.sub}</span>
                </div>
              </div>
            ))}
          </div>

          <div className="lp-ctas">
            <button className="lp-cta-main" onClick={() => navigate("story")}>
              <span>Start Exploring</span>
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                <path d="M5 12h14M12 5l7 7-7 7" />
              </svg>
            </button>
          </div>
        </div>

        <div className={`lp-right ${loaded ? "lp-right--in" : ""}`}>
          <IndiaMapHero />
        </div>
      </div>

      <div className="lp-ticker">
        <div className="lp-ticker-track">
          {[...TICKER_ITEMS, ...TICKER_ITEMS].map((item, i) => (
            <span key={i} className="lp-ticker-item">
              <span className="lp-ticker-dot" />{item}
            </span>
          ))}
        </div>
      </div>
    </div>
  );
}